import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import api, { formatApiError } from "@/lib/apiClient";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

const empty = { username: "", contact_email: "", name: "", role: "admin", password: "" };

export default function AdminUsers() {
  const { lang } = useLang();
  const [list, setList] = useState([]);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/users").then(r => setList(r.data));
  useEffect(() => { load(); }, []);

  const create = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (editing && !payload.password) delete payload.password;
      if (editing) await api.put(`/admin/users/${editing}`, payload);
      else await api.post("/admin/users", payload);
      toast.success(lang === "id" ? "Akun staff disimpan" : "Staff account saved");
      setForm(empty); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };
  const remove = async (id) => {
    try { await api.delete(`/admin/users/${id}`); load(); toast.success(lang === "id" ? "Dipindahkan ke Sampah" : "Moved to Trash"); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
  };
  const changeRole = async (id, role) => {
    try {
      await api.put(`/admin/users/${id}`, { role });
      load(); toast.success(lang === "id" ? "Peran diperbarui" : "Role updated");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
  };

  return (
    <AdminLayout>
      <h1 className="font-display text-3xl tracking-tight mb-8">{lang === "id" ? "Akun Staff" : "Staff Accounts"}</h1>
      <div className="grid lg:grid-cols-3 gap-6">
        <form onSubmit={create} className="p-6 rounded-2xl border border-border bg-card space-y-3">
          <div className="font-display text-lg">{lang === "id" ? "Kelola akun staff" : "Manage staff account"}</div>
          <Field htmlFor="staff-username" label="Username *"><Input id="staff-username" data-testid="user-username" autoComplete="off" required value={form.username} onChange={(e) => setForm({...form, username: e.target.value})} /></Field>
          <Field htmlFor="staff-contact" label={lang === "id" ? "Email kontak (opsional)" : "Contact email (optional)"}><Input id="staff-contact" data-testid="user-contact-email" type="email" value={form.contact_email} onChange={(e) => setForm({...form, contact_email: e.target.value})} /></Field>
          <Field htmlFor="staff-name" label={lang === "id" ? "Nama" : "Name"}><Input id="staff-name" data-testid="user-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} /></Field>
          <Field htmlFor="staff-role" label={lang === "id" ? "Peran *" : "Role *"}>
            <Select value={form.role} onValueChange={(v) => setForm({...form, role: v})}>
              <SelectTrigger id="staff-role" data-testid="user-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="reviewer">Reviewer</SelectItem>
                <SelectItem value="researcher">Researcher</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field htmlFor="staff-password" label={lang === "id" ? "Kata sandi (min 8; wajib untuk akun baru)" : "Password (min 8; required for new accounts)"}>
            <Input id="staff-password" autoComplete="new-password" required={!editing} minLength={8} data-testid="user-password" type="password" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} />
          </Field>
          <Button data-testid="user-create-btn" type="submit" disabled={saving} className="rounded-full bg-terracotta-500 hover:bg-terracotta-600 text-cream-50 w-full">
            <Plus className="w-4 h-4 mr-1" /> {editing ? (lang === "id" ? "Simpan" : "Save") : (lang === "id" ? "Tambah" : "Add")}
          </Button>
          {editing && <Button type="button" variant="ghost" onClick={() => { setEditing(null); setForm(empty); }}>{lang === "id" ? "Batal" : "Cancel"}</Button>}
        </form>
        <div className="lg:col-span-2 rounded-2xl border border-border bg-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/60 text-xs uppercase tracking-wider text-muted-foreground">
              <tr><th className="text-left p-4">Username</th><th className="text-left p-4">{lang === "id" ? "Nama" : "Name"}</th><th className="text-left p-4">{lang === "id" ? "Peran" : "Role"}</th><th className="text-left p-4">{lang === "id" ? "Email kontak" : "Contact email"}</th><th className="text-left p-4">2FA</th><th className="text-right p-4">{lang === "id" ? "Tindakan" : "Actions"}</th></tr>
            </thead>
            <tbody className="divide-y divide-border">
              {list.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.username}`}>
                  <td className="p-4 font-semibold">{u.username}</td>
                  <td className="p-4">{u.name}</td>
                  <td className="p-4">
                    <Select value={u.role} onValueChange={(v) => changeRole(u.id, v)}>
                      <SelectTrigger data-testid={`user-role-${u.username}`} className="h-8 w-32"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="reviewer">Reviewer</SelectItem>
                        <SelectItem value="researcher">Researcher</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="p-4">{u.contact_email || "—"}</td>
                  <td className="p-4 text-xs text-muted-foreground">{u.totp_enabled ? "✓" : "—"}</td>
                  <td className="p-4 text-right">
                    <Button size="sm" variant="ghost" onClick={() => { setEditing(u.id); setForm({username:u.username, contact_email:u.contact_email || "", name:u.name || "", role:u.role, password:""}); }}>{lang === "id" ? "Ubah" : "Edit"}</Button>
                    <Button size="sm" variant="ghost" className="text-destructive" onClick={() => remove(u.id)} data-testid={`user-delete-${u.username}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}

const Field = ({ label, children, htmlFor }) => (
  <div className="space-y-1">
    <Label htmlFor={htmlFor} className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>
    {children}
  </div>
);
